import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { MapPin, Phone, Mail, Heart } from 'lucide-react';

export const Footer = () => {
  const { t, language } = useLanguage();

  const paypalDonateUrl = "https://www.paypal.com/donate?business=Hortensiangoundo86%40yahoo.com&currency_code=EUR";

  return (
    <footer className="bg-slate-900 text-white mt-24">
      <div className="container mx-auto px-4 md:px-8 lg:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src="/logo.jpg" alt="AILE" className="h-12 w-auto" />
              <span className="font-bold text-xl">AILE</span>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{t.footer.quickLinks}</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-slate-300 hover:text-white transition-colors text-sm">{t.nav.home}</Link></li>
              <li><Link to="/services" className="text-slate-300 hover:text-white transition-colors text-sm">{t.nav.services}</Link></li>
              <li><Link to="/listings" className="text-slate-300 hover:text-white transition-colors text-sm">{t.nav.listings}</Link></li>
              <li><Link to="/news" className="text-slate-300 hover:text-white transition-colors text-sm">{t.nav.news}</Link></li>
              <li><Link to="/about" className="text-slate-300 hover:text-white transition-colors text-sm">{t.nav.about}</Link></li>
              <li><Link to="/contact" className="text-slate-300 hover:text-white transition-colors text-sm">{t.nav.contact}</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{t.footer.contactInfo}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <MapPin size={16} className="mt-1 flex-shrink-0" />
                <span>1 place Abbé Pierre, 69009 Lyon</span>
              </li>
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <Phone size={16} />
                <a href="tel:+33681652538" className="hover:text-white transition-colors">+33 6 81 65 25 38</a>
              </li>
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <Mail size={16} />
                <a href="mailto:contact@aile-france.fr" className="hover:text-white transition-colors">contact@aile-france.fr</a>
              </li>
            </ul>
          </div>

          {/* Donate Section */}
          <div>
            <h3 className="font-semibold text-lg mb-4">
              {language === 'fr' ? 'Soutenez-nous' : 'Support us'}
            </h3>
            <p className="text-slate-300 text-sm mb-4">
              {language === 'fr' 
                ? "Aidez-nous à accompagner encore plus d'étudiants internationaux à Lyon."
                : 'Help us support even more international students in Lyon.'}
            </p>
            <a
              href={paypalDonateUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FFC439] hover:bg-[#F0B429] text-slate-900 font-semibold px-6 py-3 rounded-full transition-all hover:scale-105 shadow-lg"
              data-testid="donate-btn-footer"
            >
              <Heart size={18} className="text-red-500" />
              {language === 'fr' ? 'Faire un don' : 'Donate'}
            </a>
            <p className="text-slate-500 text-xs mt-3">
              {language === 'fr' ? 'Paiement sécurisé via PayPal' : 'Secure payment via PayPal'}
            </p>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="border-t border-slate-800 mt-8 pt-6">
          {/* Copyright + Webysee */}
          <div className="text-center mb-4">
            <p className="text-slate-400 text-sm mb-2">{t.footer.copyright}</p>
            <p className="text-slate-500 text-xs">
              Créé par{' '}
              <a 
                href="https://webysee.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 hover:underline font-medium transition-colors"
              >
                webysee.com
              </a>
            </p>
          </div>
          
          {/* Legal Links */}
          <div className="flex items-center justify-center gap-6 text-slate-400 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">
              {language === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}
            </Link>
            <Link to="/legal" className="hover:text-white transition-colors">
              {language === 'fr' ? 'Mentions légales' : 'Legal Notice'}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};